/**
 * Session state for the parent portal.
 *
 * The token is whatever /api/auth/authenticate or /api/auth/otp/verify minted
 * — a normal PocketBase record auth token. We keep it in localStorage so a
 * refresh doesn't sign the parent out, and drop it the moment the backend
 * answers 401.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as api from './api.js';
import { disableGoogleAutoSelect } from './google.js';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => api.getToken());
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(Boolean(api.getToken()));
  const [error, setError] = useState(null);

  const signOut = useCallback(() => {
    api.setToken(null);
    disableGoogleAutoSelect();
    setTokenState(null);
    setUser(null);
    setProfiles([]);
    setError(null);
  }, []);

  /** Pull the account snapshot. Signs out on 401 — the token is gone or stale. */
  const refresh = useCallback(async (signal) => {
    if (!api.getToken()) {
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const data = await api.getUserInfo(signal);
      setUser(data?.user_record ?? null);
      setProfiles(data?.profiles ?? []);
      setError(null);
      return data;
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      if (err?.status === 401 || err?.status === 403) {
        signOut();
        return null;
      }
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  // Adopt a freshly minted token from either sign-in door.
  const adoptSession = useCallback(async (result) => {
    if (!result?.token) throw new Error('missing_token');
    api.setToken(result.token);
    setTokenState(result.token);
    await refresh();
  }, [refresh]);

  const signInWithGoogle = useCallback(async (idToken) => {
    await adoptSession(await api.authenticateWithGoogle(idToken));
  }, [adoptSession]);

  const signInWithOtp = useCallback(async (otpId, code) => {
    await adoptSession(await api.verifyOtp(otpId, code));
  }, [adoptSession]);

  const signInWithPassword = useCallback(async (email, password) => {
    await adoptSession(await api.loginWithPassword(email, password));
  }, [adoptSession]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
    // Deliberately keyed on the token only: refresh is stable and we want one
    // fetch per session, not one per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = useMemo(() => ({
    isAuthed: Boolean(token),
    token,
    user,
    profiles,
    setProfiles,
    loading,
    error,
    refresh,
    signInWithGoogle,
    signInWithOtp,
    signInWithPassword,
    signOut,
  }), [token, user, profiles, loading, error, refresh, signInWithGoogle, signInWithOtp, signInWithPassword, signOut]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth() must be inside <AuthProvider>');
  return v;
}
